import React, {
  MouseEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Badge,
  Box,
  Chip,
  ContainerColor,
  Header,
  Icon,
  IconButton,
  Icons,
  Menu,
  MenuItem,
  PopOut,
  RectCords,
  Scroll,
  Spinner,
  Text,
  Tooltip,
  TooltipProvider,
  config,
} from 'folds';
import { Room, RoomMember, MatrixEvent } from 'matrix-js-sdk';
import { useVirtualizer } from '@tanstack/react-virtual';
import FocusTrap from 'focus-trap-react';
import classNames from 'classnames';

import { openProfileViewer, openInviteByRole } from '../../../client/action/navigation';
import * as css from './MembersDrawer.css';
import { useMatrixClient } from '../../hooks/useMatrixClient';
import { Membership } from '../../../types/matrix/room';
import { UseStateProvider } from '../../components/UseStateProvider';
import { usePowerLevelTags, PowerLevelTag } from '../../hooks/usePowerLevelTags';
import { TypingIndicator } from '../../components/typing-indicator';
import { getMemberDisplayName } from '../../utils/room';
import { getMxIdLocalPart } from '../../utils/matrix';
import { useSetSetting, useSetting } from '../../state/hooks/settings';
import { settingsAtom } from '../../state/settings';
import { millify } from '../../plugins/millify';
import { ScrollTopContainer } from '../../components/scroll-top-container';
import { UserAvatar } from '../../components/user-avatar';
import { useRoomTypingMember } from '../../hooks/useRoomTypingMembers';
import { stopPropagation } from '../../utils/keyboard';
import { useMediaAuthentication } from '../../hooks/useMediaAuthentication';
import { usePowerLevels, usePowerLevelsAPI } from '../../hooks/usePowerLevels';
import { debugError } from '../../utils/debugLog';
import { fetchDefaultContacts } from './CurveWork';

export const MembershipFilters = {
  filterJoined: (m: RoomMember) => m.membership === Membership.Join,
  filterInvited: (m: RoomMember) => m.membership === Membership.Invite,
  filterLeaved: (m: RoomMember) =>
    m.membership === Membership.Leave &&
    m.events.member?.getStateKey() === m.events.member?.getSender(),
  filterKicked: (m: RoomMember) =>
    m.membership === Membership.Leave &&
    m.events.member?.getStateKey() !== m.events.member?.getSender(),
  filterBanned: (m: RoomMember) => m.membership === Membership.Ban,
};

export type MembershipFilterFn = (m: RoomMember) => boolean;

export type MembershipFilter = {
  name: string;
  filterFn: MembershipFilterFn;
  color: ContainerColor;
};

const useMembershipFilterMenu = (t: (key: string) => string): MembershipFilter[] =>
  useMemo(
    () => [
      {
        name: t('MembersDrawer.membership_joined'),
        filterFn: MembershipFilters.filterJoined,
        color: 'Background',
      },
      {
        name: t('MembersDrawer.membership_invited'),
        filterFn: MembershipFilters.filterInvited,
        color: 'Success',
      },
      {
        name: t('MembersDrawer.membership_left'),
        filterFn: MembershipFilters.filterLeaved,
        color: 'Secondary',
      },
      {
        name: t('MembersDrawer.membership_kicked'),
        filterFn: MembershipFilters.filterKicked,
        color: 'Warning',
      },
      {
        name: t('MembersDrawer.membership_banned'),
        filterFn: MembershipFilters.filterBanned,
        color: 'Critical',
      },
    ],
    [t]
  );

export const SortFilters = {
  filterAscending: (a: RoomMember, b: RoomMember) =>
    a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1,
  filterDescending: (a: RoomMember, b: RoomMember) =>
    a.name.toLowerCase() > b.name.toLowerCase() ? -1 : 1,
  filterNewestFirst: (a: RoomMember, b: RoomMember) =>
    (b.events.member?.getTs() ?? 0) - (a.events.member?.getTs() ?? 0),
  filterOldest: (a: RoomMember, b: RoomMember) =>
    (a.events.member?.getTs() ?? 0) - (b.events.member?.getTs() ?? 0),
};

export type SortFilterFn = (a: RoomMember, b: RoomMember) => number;

export type SortFilter = {
  name: string;
  filterFn: SortFilterFn;
};

const useSortFilterMenu = (t: (key: string) => string): SortFilter[] =>
  useMemo(
    () => [
      {
        name: t('MembersDrawer.sort_a_to_z'),
        filterFn: SortFilters.filterAscending,
      },
      {
        name: t('MembersDrawer.sort_z_to_a'),
        filterFn: SortFilters.filterDescending,
      },
      {
        name: t('MembersDrawer.sort_newest'),
        filterFn: SortFilters.filterNewestFirst,
      },
      {
        name: t('MembersDrawer.sort_oldest'),
        filterFn: SortFilters.filterOldest,
      },
    ],
    [t]
  );

export type MembersFilterOptions = {
  membershipFilter: MembershipFilter;
  sortFilter: SortFilter;
};

type MembersDrawerProps = {
  room: Room;
  members: RoomMember[];
};

export function MembersDrawer({ room, members }: MembersDrawerProps) {
  const { t } = useTranslation();
  const mx = useMatrixClient();
  const useAuthentication = useMediaAuthentication();
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollTopAnchorRef = useRef<HTMLDivElement>(null);
  const getPowerLevelTag = usePowerLevelTags();
  const fetchingMembers = members.length < room.getJoinedMemberCount();
  const setPeopleDrawer = useSetSetting(settingsAtom, 'isPeopleDrawer');

  const membershipFilterMenu = useMembershipFilterMenu(t);
  const sortFilterMenu = useSortFilterMenu(t);
  const [sortFilterIndex, setSortFilterIndex] = useSetting(settingsAtom, 'memberSortFilterIndex');
  const [membershipFilterIndex, setMembershipFilterIndex] = useState(0);
  // const [editMode, setEditMode] = useState(false);

  const membershipFilter = membershipFilterMenu[membershipFilterIndex] ?? membershipFilterMenu[0];
  const sortFilter = sortFilterMenu[sortFilterIndex] ?? sortFilterMenu[0];

  const typingMembers = useRoomTypingMember(room.roomId);

  const powerLevels = usePowerLevels(room);
  const { getPowerLevel, canDoAction } = usePowerLevelsAPI(powerLevels);
  const myUserId = mx.getUserId() ?? undefined;
  const myPowerLevel = getPowerLevel(myUserId);
  // TODO: 啟用編輯模式時使用（目前編輯按鈕已暫時停用）
  // const canKick = canDoAction('kick', myPowerLevel);

  // 確認目前使用者是否有邀請權限
  const canInvite = room.canInvite(myUserId ?? '');

  // 確認目前使用者是否為預設聯絡窗口
  const [isDefaultContact, setIsDefaultContact] = useState(false);

  const checkPermission = useCallback(async () => {
    const projectEvent = room.currentState.getStateEvents('m.room.project', '');
    const project = projectEvent?.getContent()?.project;
    if (!project || !myUserId) {
      setIsDefaultContact(false);
      return;
    }

    try {
      const contacts = await fetchDefaultContacts(mx, project);
      const localPart = getMxIdLocalPart(myUserId);
      const isContact = contacts.some((c) => c.UserName === localPart);
      setIsDefaultContact(isContact);
    } catch (e) {
      debugError('MembersDrawer: checkPermission failed:', e);
      setIsDefaultContact(false);
    }
  }, [room, mx, myUserId]);

  useEffect(() => {
    checkPermission();
  }, [checkPermission]);

  useEffect(() => {
    const handleRoomStateUpdate = (event: MatrixEvent) => {
      if (event.getRoomId() !== room.roomId) return;
      if (event.getType() === 'm.room.project') {
        checkPermission();
      }
    };
    mx.on('RoomState.events', handleRoomStateUpdate);
    return () => {
      mx.removeListener('RoomState.events', handleRoomStateUpdate);
    };
  }, [mx, room.roomId, checkPermission]);

  const filteredMembers = useMemo(
    () =>
      members
        .filter(membershipFilter.filterFn)
        .sort(sortFilter.filterFn)
        .sort((a, b) => b.powerLevel - a.powerLevel),
    [members, membershipFilter, sortFilter]
  );

  const PLTagOrRoomMember = useMemo(() => {
    let prevTag: PowerLevelTag | undefined;
    const tagOrMember: Array<PowerLevelTag | RoomMember> = [];
    filteredMembers.forEach((m) => {
      const plTag = getPowerLevelTag(m.powerLevel);
      if (plTag !== prevTag) {
        prevTag = plTag;
        tagOrMember.push(plTag);
      }
      tagOrMember.push(m);
    });
    return tagOrMember;
  }, [filteredMembers, getPowerLevelTag]);

  const virtualizer = useVirtualizer({
    count: PLTagOrRoomMember.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 40,
    overscan: 10,
  });

  const getName = (member: RoomMember) =>
    getMemberDisplayName(room, member.userId) ?? getMxIdLocalPart(member.userId) ?? member.userId;

  const handleMemberClick: MouseEventHandler<HTMLButtonElement> = (evt) => {
    const btn = evt.currentTarget as HTMLButtonElement;
    const userId = btn.getAttribute('data-user-id');
    openProfileViewer(userId, room.roomId);
  };

  const handleKick = (userId: string) => {
    mx.kick(room.roomId, userId).catch((err) => {
      debugError('MembersDrawer: kick failed:', err);
    });
  };

  // const handleLeave = () => {
  //   mx.leave(room.roomId).catch((err) => {
  //     debugError('MembersDrawer: leave failed:', err);
  //   });
  // };

  return (
    <Box className={css.MembersDrawer} shrink="No" direction="Column">
      <Header className={css.MembersDrawerHeader} variant="Background" size="600">
        <Box grow="Yes" alignItems="Center" gap="200">
          <Box grow="Yes" alignItems="Center" gap="200">
            <Text title={t('MembersDrawer.members_count', { total: room.getJoinedMemberCount() })} size="H5" truncate>
              {t('MembersDrawer.members_count', { total: millify(room.getJoinedMemberCount()) })}
            </Text>
          </Box>
          <Box shrink="No" alignItems="Center">
            <TooltipProvider
              position="Bottom"
              align="End"
              offset={4}
              tooltip={
                <Tooltip>
                  <Text>{t('MembersDrawer.close')}</Text>
                </Tooltip>
              }
            >
              {(triggerRef) => (
                <IconButton
                  ref={triggerRef}
                  variant="Background"
                  onClick={() => setPeopleDrawer(false)}
                >
                  <Icon src={Icons.Cross} />
                </IconButton>
              )}
            </TooltipProvider>
          </Box>
        </Box>
      </Header>
      <Box className={css.MemberDrawerContentBase} grow="Yes">
        <Scroll ref={scrollRef} variant="Background" size="300" visibility="Hover" hideTrack>
          <Box className={css.MemberDrawerContent} direction="Column" gap="200">
            <Box ref={scrollTopAnchorRef} className={css.DrawerGroup} direction="Column" gap="200">
              <Box alignItems="Center" justifyContent="SpaceBetween" gap="200">
                <UseStateProvider initial={undefined}>
                  {(anchor: RectCords | undefined, setAnchor) => (
                    <PopOut
                      anchor={anchor}
                      position="Bottom"
                      align="Start"
                      offset={4}
                      content={
                        <FocusTrap
                          focusTrapOptions={{
                            initialFocus: false,
                            onDeactivate: () => setAnchor(undefined),
                            clickOutsideDeactivates: true,
                            isKeyForward: (evt: KeyboardEvent) => evt.key === 'ArrowDown',
                            isKeyBackward: (evt: KeyboardEvent) => evt.key === 'ArrowUp',
                            escapeDeactivates: stopPropagation,
                          }}
                        >
                          <Menu style={{ padding: config.space.S100 }}>
                            {membershipFilterMenu.map((menuItem, index) => (
                              <MenuItem
                                key={menuItem.name}
                                variant={
                                  menuItem.name === membershipFilter.name
                                    ? menuItem.color
                                    : 'Surface'
                                }
                                aria-pressed={menuItem.name === membershipFilter.name}
                                size="300"
                                radii="300"
                                onClick={() => {
                                  setMembershipFilterIndex(index);
                                  setAnchor(undefined);
                                }}
                              >
                                <Text size="T300">{menuItem.name}</Text>
                              </MenuItem>
                            ))}
                          </Menu>
                        </FocusTrap>
                      }
                    >
                      <Chip
                        onClick={
                          ((evt) =>
                            setAnchor(
                              evt.currentTarget.getBoundingClientRect()
                            )) as MouseEventHandler<HTMLButtonElement>
                        }
                        variant={membershipFilter.color}
                        size="400"
                        radii="300"
                        before={<Icon src={Icons.Filter} size="50" />}
                      >
                        <Text size="T200">{membershipFilter.name}</Text>
                      </Chip>
                    </PopOut>
                  )}
                </UseStateProvider>
                <UseStateProvider initial={undefined}>
                  {(anchor: RectCords | undefined, setAnchor) => (
                    <PopOut
                      anchor={anchor}
                      position="Bottom"
                      align="End"
                      offset={4}
                      content={
                        <FocusTrap
                          focusTrapOptions={{
                            initialFocus: false,
                            onDeactivate: () => setAnchor(undefined),
                            clickOutsideDeactivates: true,
                            isKeyForward: (evt: KeyboardEvent) => evt.key === 'ArrowDown',
                            isKeyBackward: (evt: KeyboardEvent) => evt.key === 'ArrowUp',
                            escapeDeactivates: stopPropagation,
                          }}
                        >
                          <Menu style={{ padding: config.space.S100 }}>
                            {sortFilterMenu.map((menuItem, index) => (
                              <MenuItem
                                key={menuItem.name}
                                variant="Surface"
                                aria-pressed={menuItem.name === sortFilter.name}
                                size="300"
                                radii="300"
                                onClick={() => {
                                  setSortFilterIndex(index);
                                  setAnchor(undefined);
                                }}
                              >
                                <Text size="T300">{menuItem.name}</Text>
                              </MenuItem>
                            ))}
                          </Menu>
                        </FocusTrap>
                      }
                    >
                      <Chip
                        onClick={
                          ((evt) =>
                            setAnchor(
                              evt.currentTarget.getBoundingClientRect()
                            )) as MouseEventHandler<HTMLButtonElement>
                        }
                        variant="Background"
                        size="400"
                        radii="300"
                        after={<Icon src={Icons.Sort} size="50" />}
                      >
                        <Text size="T200">{sortFilter.name}</Text>
                      </Chip>
                    </PopOut>
                  )}
                </UseStateProvider>
              </Box>
            </Box>

            <ScrollTopContainer scrollRef={scrollRef} anchorRef={scrollTopAnchorRef}>
              <IconButton
                onClick={() => virtualizer.scrollToOffset(0)}
                variant="Surface"
                radii="Pill"
                outlined
                size="300"
                aria-label="Scroll to Top"
              >
                <Icon src={Icons.ChevronTop} size="300" />
              </IconButton>
            </ScrollTopContainer>

            {!fetchingMembers && filteredMembers.length === 0 && (
              <Text style={{ padding: config.space.S300 }} align="Center">
                {t('MembersDrawer.no_members_filter', { filter: membershipFilter.name })}
              </Text>
            )}

            <Box className={css.MembersGroup} direction="Column" gap="100">
              <div
                style={{
                  position: 'relative',
                  height: virtualizer.getTotalSize(),
                }}
              >
                {virtualizer.getVirtualItems().map((vItem) => {
                  const tagOrMember = PLTagOrRoomMember[vItem.index];
                  if (!('userId' in tagOrMember)) {
                    return (
                      <Text
                        style={{
                          transform: `translateY(${vItem.start}px)`,
                        }}
                        data-index={vItem.index}
                        ref={virtualizer.measureElement}
                        key={`${room.roomId}-${vItem.index}`}
                        className={classNames(css.MembersGroupLabel, css.DrawerVirtualItem)}
                        size="L400"
                      >
                        {tagOrMember.name}
                      </Text>
                    );
                  }

                  const member = tagOrMember;
                  const name = getName(member);
                  const avatarMxcUrl = member.getMxcAvatarUrl();
                  const avatarUrl = avatarMxcUrl ? mx.mxcUrlToHttp(
                    avatarMxcUrl,
                    100,
                    100,
                    'crop',
                    undefined,
                    false,
                    useAuthentication
                  ) : undefined;

                  return (
                    <MenuItem
                      style={{
                        padding: `${config.space.S500} ${config.space.S200}`,
                        transform: `translateY(${vItem.start}px)`,
                      }}
                      data-index={vItem.index}
                      data-user-id={member.userId}
                      ref={virtualizer.measureElement}
                      key={`${room.roomId}-${member.userId}`}
                      className={css.DrawerVirtualItem}
                      variant="Background"
                      radii="400"
                      onClick={handleMemberClick}
                      title={name}
                      before={
                        <Avatar size="200">
                          <UserAvatar
                            userId={member.userId}
                            src={avatarUrl ?? undefined}
                            alt={name}
                            renderFallback={() => <Icon size="50" src={Icons.User} filled />}
                          />
                        </Avatar>
                      }
                      after={
                        typingMembers.find((receipt) => receipt.userId === member.userId) && (
                          <Badge size="300" variant="Secondary" fill="Soft" radii="Pill" outlined>
                            <TypingIndicator size="300" />
                          </Badge>
                        )
                      }
                    >
                      <Box grow="Yes" direction="Column" gap="100" justifyContent="Center">
                        <Text size="T400" truncate>
                          {name}
                        </Text>
                      </Box>
                    </MenuItem>
                  );
                })}
              </div>
            </Box>

            {fetchingMembers && (
              <Box justifyContent="Center">
                <Spinner />
              </Box>
            )}
          </Box>
        </Scroll>
      </Box>

      <Box className={css.InviteSection} gap="200" alignItems="Center" justifyContent="End">
        {canInvite && isDefaultContact && (
          <Chip
            onClick={() => openInviteByRole(room.roomId)}
            variant="Success"
            size="400"
            radii="300"
          >
            <Text size="T200">加入</Text>
          </Chip>
        )}

        {/* 編輯按鈕（暫時停用） */}
        {/* {canKick && (
          <Chip
            onClick={() => setEditMode(!editMode)}
            variant={editMode ? 'Warning' : 'Background'}
            size="400"
            radii="300"
          >
            <Text size="T200">{editMode ? '完成' : '編輯'}</Text>
          </Chip>
        )} */}

        {/* 退出按鈕（暫時停用，目前不提供退出功能） */}
        {/* <Chip
          onClick={handleLeave}
          variant="Critical"
          size="400"
          radii="300"
        >
          <Text size="T200">退出</Text>
        </Chip> */}
      </Box>
    </Box>
  );
}
